import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, QrCode, Link2, CheckCircle2, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const qrSrc = (url) => `https://api.qrserver.com/v1/create-qr-code/?size=170x170&data=${encodeURIComponent(url)}`;

export function DepositControl({ booking }) {
  const [open, setOpen] = useState(false);
  const [hold, setHold] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/admin/bookings/${booking.id}/deposit`); setHold(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async () => {
    const n = !open; setOpen(n);
    if (n && !hold) await load();
  };

  const authorize = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/deposit/authorize`, { origin_url: window.location.origin });
      toast.success(`Richiesta cauzione €${data.amount} creata`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  const release = async () => {
    setLoading(true);
    try { await api.post(`/admin/bookings/${booking.id}/deposit/release`); toast.success("Cauzione rilasciata (nessun addebito)"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  const capture = async () => {
    const raw = window.prompt(`Importo penale da trattenere (max €${hold?.amount}). Lascia vuoto per trattenere tutta la cauzione:`);
    if (raw === null) return;
    const amount = raw.trim() ? Number(raw.replace(",", ".")) : null;
    setLoading(true);
    try { const { data } = await api.post(`/admin/bookings/${booking.id}/deposit/capture`, { amount }); toast.success(`Penale €${data.captured_amount} addebitata`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  const st = hold?.status;
  const badge = st === "authorized" ? { t: `Cauzione bloccata €${hold.amount}`, c: "text-amber-700 bg-amber-50 border-amber-200" }
    : st === "captured" ? { t: `Penale trattenuta €${hold.captured_amount}`, c: "text-red-700 bg-red-50 border-red-200" }
    : st === "released" ? { t: "Cauzione rilasciata", c: "text-emerald-700 bg-emerald-50 border-emerald-200" }
    : st === "pending" ? { t: "In attesa autorizzazione ospite", c: "text-blue-700 bg-blue-50 border-blue-200" }
    : { t: "Cauzione differenziata", c: "text-fir-deep/60 bg-stone/40 border-stone" };

  return (
    <div className="mt-1.5">
      <button data-testid="deposit-toggle-btn" onClick={toggle}
        className={`flex items-center gap-1 text-xs font-medium border rounded-lg px-2 py-1 transition-colors ${badge.c}`}>
        <ShieldCheck size={12} /> {badge.t}
      </button>

      {open && (
        <div className="mt-2 p-3 rounded-xl border border-stone bg-white w-64" data-testid="deposit-panel">
          {(!st || st === "none") && (
            <button data-testid="deposit-authorize-btn" onClick={authorize} disabled={loading}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-medium bg-gold text-fir-deep rounded-lg px-3 py-2 hover:bg-gold/90 disabled:opacity-50">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <QrCode size={13} />} Richiedi cauzione (QR/link)
            </button>
          )}

          {(st === "pending" || st === "authorized") && hold?.url && (st === "pending") && (
            <div className="text-center">
              <p className="text-xs text-fir-deep/60 mb-2">Fai inquadrare il QR all'ospite al check-in:</p>
              <img src={qrSrc(hold.url)} alt="QR cauzione" className="mx-auto rounded-lg border border-stone" width={150} height={150} />
              <a href={hold.url} target="_blank" rel="noopener noreferrer" data-testid="deposit-link"
                className="mt-2 inline-flex items-center gap-1 text-xs text-wood hover:underline"><Link2 size={12} /> Apri link</a>
              <button onClick={load} className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-fir-deep/70 hover:text-fir">
                <RefreshCw size={12} /> Verifica autorizzazione
              </button>
            </div>
          )}

          {st === "authorized" && (
            <div className="space-y-2">
              <p className="text-xs text-fir-deep/70">L'ospite ha autorizzato €{hold.amount}. Dopo il controllo:</p>
              <button data-testid="deposit-release-btn" onClick={release} disabled={loading}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg px-3 py-2 hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 size={13} /> Differenziata OK — Rilascia
              </button>
              <button data-testid="deposit-capture-btn" onClick={capture} disabled={loading}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-medium bg-red-600 text-white rounded-lg px-3 py-2 hover:bg-red-700 disabled:opacity-50">
                <AlertTriangle size={13} /> Trattieni penale
              </button>
            </div>
          )}

          {(st === "released" || st === "captured") && (
            <p className="text-xs text-fir-deep/70">{st === "released" ? "Cauzione rilasciata: nessun addebito." : `Penale di €${hold.captured_amount} addebitata all'ospite.`}</p>
          )}
        </div>
      )}
    </div>
  );
}
